/* Copyright (c) 2020-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import { useMemo } from "react";
import dayjs from "dayjs";
import { Visible } from "react-awesome-styled-grid";
import { useParams } from "react-router-dom";
import {
  Row,
  Col,
  Box,
  Spacer,
  theme,
  Tabs,
  Tab,
  FlexContainer,
} from "@nordcloud/gnui";
import { useOrgUnitApplicationsQuery } from "~/generated/graphql";
import {
  BreadcrumbsBox,
  UniversalWrap,
  TableLoader,
  LoaderWrap,
  ResponsiveBrickLoader,
  Pagination,
} from "~/components";
import { CostBox, CustomComponents } from "~/components/CostBox";
import { dateFormat } from "~/constants";
import {
  useUuidValidator,
  useTabsWithUrl,
  useScroll,
  useScrollDisclosure,
  useQueryState,
} from "~/hooks";
import { ROUTES } from "~/routing/routes";
import { CostChart, OrganizationUnitEditableDetails } from "./Details";
import { useOrgUnitWithChildren } from "./hooks";
import { ApplicationList, OrganizationUnitStructure } from "./Lists";
import { OrganizationUnitControls } from "./OrganizationUnitControls";
import { OrgUnitCostTabs } from "./OrgUnitCostTabs";
import { RouteParams } from "./types";

const year = dayjs().format(dateFormat.year);

function useOrgUnitApplications({
  orgUnitId,
  limit,
  page,
}: {
  orgUnitId: string;
  limit: number;
  page: number;
}) {
  const { data, ...rest } = useOrgUnitApplicationsQuery({
    variables: { orgUnitId, year, page, limit },
  });

  const applications = data?.orgUnitApplications?.results ?? [];
  const applicationIds = applications.map((app) => app.id) ?? [];
  const applicationsCount = data?.orgUnitApplications?.count ?? 0;
  const applicationsPages = data?.orgUnitApplications?.pages ?? 0;

  return {
    data: {
      applications,
      applicationIds,
      applicationsCount,
      applicationsPages,
    },
    ...rest,
  };
}

export function OrganizationUnit() {
  const { structureId, unitId } = useParams<RouteParams>();

  const preservedBcId = useMemo(() => structureId, []);

  useUuidValidator(unitId);

  const {
    data: {
      costs,
      label,
      orgUnitDetails,
      progress,
      isForecast,
      tags,
      orgUnitChildren,
      businessContext,
    },
    isEmptyData,
    loading: isOrgUnitWithChildrenLoading,
    error: orgUnitWithChildrenError,
  } = useOrgUnitWithChildren(unitId, year);

  const {
    set: setCostTab,
    state: { page, limit },
  } = useQueryState<{ costTab: "budget" }>();

  const {
    data: { applicationIds, applications, applicationsCount },
    loading: isOrgUnitApplicationsLoading,
    error: orgUnitApplicationsError,
    refetch,
  } = useOrgUnitApplications({
    orgUnitId: unitId,
    page,
    limit,
  });

  const { activeTab, setActiveTab } = useTabsWithUrl();

  const businessContextLabel = businessContext?.name ?? structureId;

  const breadcrumbs = [
    {
      label: "Home",
      uri: "/",
    },
    {
      label: "Business Context",
      uri: `/business-contexts/${preservedBcId}`,
      isDisabled: true,
    },
    {
      label: businessContextLabel,
      uri: `/business-contexts/${preservedBcId}`,
    },
    {
      label,
      uri: `/business-contexts/${preservedBcId}/${unitId}`,
    },
  ];

  const { triggerScroll, scrollToRef, isScrolled, setIsScrolled } =
    useScrollDisclosure();

  useScroll({
    ref: scrollToRef,
    smooth: true,
    trigger: () => isScrolled,
    onFinish: () => {
      setIsScrolled(false);
      setCostTab("costTab")("budget");
    },
  });

  return (
    <Row>
      <Col>
        <UniversalWrap
          errorProps={{
            error: isEmptyData ? orgUnitWithChildrenError : undefined,
          }}
          loaderProps={{
            loading: isOrgUnitWithChildrenLoading,
            Component: OrgUnitDetailsLoader,
          }}
        >
          <BreadcrumbsBox
            title={label}
            tags={tags}
            customBreadcrumbs={breadcrumbs}
          >
            <OrganizationUnitControls
              nid={unitId}
              redirectUrl={`${ROUTES.businessContexts.index}/${structureId}`}
              name={label}
            />
          </BreadcrumbsBox>
          <Box spacing="spacing04">
            <Row>
              <Col xs={8} sm={8} md={5} lg={8}>
                <FlexContainer css={{ height: "100%" }}>
                  <CostChart
                    progress={progress}
                    showBudget={(costs.budget ?? 0) > 0}
                    isForecast={isForecast}
                  />
                  <OrganizationUnitEditableDetails {...orgUnitDetails} />
                </FlexContainer>
              </Col>
              <Visible sm xs>
                <Spacer height={theme.spacing.spacing04} width="100%" />
              </Visible>

              <Col sm={8} md={3} lg={4}>
                <CostBox
                  numericalValues={costs}
                  customComponent={CustomComponents.BUDGET}
                  customComponentConfig={{ scrollToBudget: triggerScroll }}
                />
              </Col>
            </Row>
          </Box>
        </UniversalWrap>

        <div ref={scrollToRef}>
          <LoaderWrap
            Component={OrgUnitCostTabsLoader}
            loading={
              isOrgUnitWithChildrenLoading || isOrgUnitApplicationsLoading
            }
          >
            <OrgUnitCostTabs
              applicationIds={applicationIds}
              forecast={costs.forecast}
            />
          </LoaderWrap>
        </div>
        <Spacer height={theme.spacing.spacing04} />

        <Tabs step={activeTab} handleTab={setActiveTab}>
          <Tab
            heading="Environments"
            caption="Environments attached to Organizational Unit"
          >
            <UniversalWrap
              errorProps={{ error: orgUnitApplicationsError }}
              loaderProps={{
                loading: isOrgUnitApplicationsLoading,
                Component: ApplicationListLoader,
              }}
            >
              <ApplicationList
                applications={applications}
                refetch={refetch}
                orgUnitId={unitId}
              />
              <Pagination count={applicationsCount} />
            </UniversalWrap>
          </Tab>
          <Tab
            heading="Organizational Units"
            caption="Organizational Unit structure"
          >
            <UniversalWrap
              errorProps={{
                error: isEmptyData ? orgUnitWithChildrenError : undefined,
              }}
              loaderProps={{
                loading: isOrgUnitWithChildrenLoading,
                Component: OrgUnitStructureLoader,
              }}
            >
              <OrganizationUnitStructure
                items={orgUnitChildren}
                unitId={unitId}
              />
            </UniversalWrap>
          </Tab>
        </Tabs>
      </Col>
    </Row>
  );
}

function OrgUnitDetailsLoader() {
  return (
    <>
      <ResponsiveBrickLoader
        height={400}
        css={{
          marginBottom: theme.spacing.spacing04,
        }}
      />
      <ResponsiveBrickLoader
        height={200}
        css={{
          marginBottom: theme.spacing.spacing04,
        }}
      />
    </>
  );
}

function OrgUnitCostTabsLoader() {
  return (
    <ResponsiveBrickLoader
      height={300}
      css={{
        marginBottom: theme.spacing.spacing04,
      }}
    />
  );
}

function ApplicationListLoader() {
  return (
    <TableLoader
      gapY={6}
      gapX={0}
      rows={[
        {
          height: 90,
          count: 5,
          items: [
            {
              widthPercent: 100,
            },
          ],
        },
      ]}
    />
  );
}

function OrgUnitStructureLoader() {
  return (
    <>
      <ResponsiveBrickLoader
        height={100}
        css={{ marginBottom: theme.spacing.spacing04 }}
      />
      <TableLoader
        gapY={10}
        gapX={6}
        rows={[
          {
            count: 8,
            height: 60,
            items: [
              {
                widthPercent: 35,
              },
              {
                widthPercent: 45,
              },
              {
                widthPercent: 15,
              },
              {
                widthPercent: 5,
              },
            ],
          },
        ]}
      />
    </>
  );
}
